import React, { useState, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  AlertTriangle, CheckCircle2, Clock, Calendar, 
  Building, TrendingUp, Loader2, RefreshCw, 
  AlertCircle, X, Trash2 
} from "lucide-react";
import { BlocoHorarioService } from '@/services/blocoHorarioService';
import { toast } from 'sonner';

interface GenerationModalProps {
  onComplete: () => void;
}

interface EstimativaGeracao {
  estimativa: number;
  relatorio: {
    totalSalas: number;
    totalBlocos: number;
    diasFuncionamento: number;
    blocosASeremCriados: number;
    blocosASeremRemovidos: number;
    blocosComReservas: number;
    proximosPeriodo: string;
  };
}

interface RelatorioGeracao {
  sucesso?: boolean;
  blocosGerados: number;
  blocosRemovidos: number;
  blocosPulados: number;
  diasProcessados: number;
  tempoExecucao: number;
  duracao?: number;
  periodoProcesamento?: {
    dataInicio: string | Date;
    dataFim: string | Date;
  };
  detalhes?: {
    por_sala?: Array<{
      sala_id: string;
      sala_nome: string;
      blocos_criados: number;
      blocos_removidos: number;
    }>;
    dias_pulados?: string[];
    warnings?: string[];
  };
}

interface GeracaoResponse {
  success?: boolean;
  message?: string;
  relatorio?: RelatorioGeracao;
}

const GenerationModal: React.FC<GenerationModalProps> = ({ onComplete }) => {
  const [step, setStep] = useState<'loading' | 'estimate' | 'generating' | 'completed'>('loading');
  const [estimativa, setEstimativa] = useState<EstimativaGeracao | null>(null);
  const [relatorio, setRelatorio] = useState<RelatorioGeracao | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const loadEstimativa = useCallback(async () => {
    try {
      setStep('loading');
      setError(null);
      const response = await BlocoHorarioService.estimarGeracaoBlocos();
      setEstimativa(response as unknown as EstimativaGeracao);
      setStep('estimate');
    } catch (error: unknown) {
      const err = error as Error;
      setError(err.message || 'Erro ao carregar estimativa');
      toast.error('Erro ao carregar estimativa');
    }
  }, []);

  useEffect(() => {
    loadEstimativa();
  }, [loadEstimativa]);

  const handleGenerate = useCallback(async () => {
    try {
      setStep('generating');
      setProgress(0);
      setError(null);

      // Simular progresso durante a geração
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 500);

      const response = await BlocoHorarioService.gerarBlocosProximos365Dias();
      
      clearInterval(progressInterval);
      setProgress(100);
      const relatorioResponse = (response as GeracaoResponse).relatorio ?? (response as RelatorioGeracao);
      const dataFim = relatorioResponse.periodoProcesamento?.dataFim;
      const dataFimFormatada = dataFim ? new Date(dataFim).toLocaleDateString('pt-BR') : '';
      const textoPeriodo = dataFimFormatada ? ` até ${dataFimFormatada}` : '';
      toast.success(`Geração concluída! ${relatorioResponse.blocosGerados} blocos criados${textoPeriodo}.`);
      setRelatorio(relatorioResponse);
      setStep('completed');
      onComplete();
    } catch (error: unknown) {
      const err = error as Error;
      setError(err.message || 'Erro na geração de blocos');
      setStep('estimate');
      toast.error('Erro na geração de blocos');
    }
  }, [onComplete]);

  const renderLoadingStep = () => (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Preparando Geração</DialogTitle>
      </DialogHeader>
      
      <div className="text-center py-6">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
        <p className="text-gray-600">Calculando estimativa...</p>
      </div>
    </DialogContent>
  );

  const renderEstimateStep = () => (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5" />
          Gerar Blocos Horários - 365 Dias
        </DialogTitle>
      </DialogHeader>
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700 font-medium">Erro</span>
          </div>
          <p className="text-red-600 mt-1">{error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadEstimativa}
            className="mt-2"
          >
            Tentar Novamente
          </Button>
        </div>
      )}

      {estimativa && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-blue-500" />
              <span className="font-medium text-blue-700">Atenção</span>
            </div>
            <p className="text-blue-600 text-sm">
              Esta operação irá gerar blocos para os próximos 365 dias úteis (segunda a sábado) 
              e remover blocos passados sem reservas. Certifique-se antes de prosseguir.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Salas</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {estimativa?.relatorio?.totalSalas || 0}
              </span>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Blocos Base</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {estimativa?.relatorio?.totalBlocos || 0}
              </span>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Dias Úteis</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {estimativa?.relatorio?.diasFuncionamento || 0}
              </span>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Total Estimado</span>
              </div>
              <span className="text-2xl font-bold text-green-600">
                {(estimativa?.estimativa || 0).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-sm font-medium text-green-700">Blocos a serem criados</span>
              <Badge className="bg-green-100 text-green-800">
                +{(estimativa?.relatorio?.blocosASeremCriados || 0).toLocaleString()}
              </Badge>
            </div>

            <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
              <span className="text-sm font-medium text-red-700">Blocos passados a remover</span>
              <Badge className="bg-red-100 text-red-800">
                -{(estimativa?.relatorio?.blocosASeremRemovidos || 0).toLocaleString()}
              </Badge>
            </div>

            {(estimativa?.relatorio?.blocosComReservas || 0) > 0 && (
              <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                <span className="text-sm font-medium text-yellow-700">Blocos com reservas (preservados)</span>
                <Badge className="bg-yellow-100 text-yellow-800">
                  {(estimativa?.relatorio?.blocosComReservas || 0).toLocaleString()}
                </Badge>
              </div>
            )}
          </div>

          <div className="bg-gray-50 border rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Período de Geração</h4>
            <p className="text-sm text-gray-600">{estimativa?.relatorio?.proximosPeriodo || 'Não disponível'}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              Isso criará blocos de tempo para os próximos 365 dias. Deseja continuar?
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Cancelar
            </Button>
            <Button onClick={handleGenerate} className="bg-green-600 hover:bg-green-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Confirmar Geração
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  );

  const renderGeneratingStep = () => (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Gerando Blocos...</DialogTitle>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Processando blocos horários...</p>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progresso</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="w-full" />
        </div>
        
        <p className="text-xs text-gray-500 text-center">
          Este processo pode levar alguns minutos...
        </p>
      </div>
    </DialogContent>
  );

  const renderCompletedStep = () => {
    const tempoExecucao = relatorio?.tempoExecucao ?? relatorio?.duracao ?? 0;
    const diasProcessados = relatorio?.diasProcessados ?? 0;
    const detalhesPorSala = relatorio?.detalhes?.por_sala ?? [];
    const warnings = relatorio?.detalhes?.warnings ?? [];
    return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          Geração Concluída
        </DialogTitle>
      </DialogHeader>
      
      {relatorio && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="font-medium text-green-700">Sucesso!</span>
            </div>
            <p className="text-green-600 text-sm">
              Geração de blocos concluída em {tempoExecucao}ms
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-600">Criados</span>
              </div>
              <span className="text-2xl font-bold text-green-600">
                {relatorio.blocosGerados.toLocaleString()}
              </span>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trash2 className="w-4 h-4 text-red-500" />
                <span className="text-sm text-gray-600">Removidos</span>
              </div>
              <span className="text-2xl font-bold text-red-600">
                {relatorio.blocosRemovidos.toLocaleString()}
              </span>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <X className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Pulados</span>
              </div>
              <span className="text-2xl font-bold text-gray-600">
                {relatorio.blocosPulados.toLocaleString()}
              </span>
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Dias</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {diasProcessados}
              </span>
            </div>
          </div>

          {detalhesPorSala.length > 0 && (
            <div className="bg-gray-50 border rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Detalhes por Sala</h4>
              <div className="space-y-2">
                {detalhesPorSala.map((sala, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-white rounded">
                    <span className="font-medium">{sala.sala_nome}</span>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-600">+{sala.blocos_criados}</span>
                      <span className="text-red-600">-{sala.blocos_removidos}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-700 mb-2">Avisos</h4>
              <ul className="text-sm text-yellow-600 space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index}>• {warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => window.location.reload()}>
              Fechar
            </Button>
          </div>
        </div>
      )}
    </DialogContent>
  );
  };

  switch (step) {
    case 'loading':
      return renderLoadingStep();
    case 'estimate':
      return renderEstimateStep();
    case 'generating':
      return renderGeneratingStep();
    case 'completed':
      return renderCompletedStep();
    default:
      return renderLoadingStep();
  }
};

export default GenerationModal; 
